import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../features/auth/hooks/useAuth';
import ViewPortfolioAsModal from '../../features/settings/components/ViewPortfolioAsModal';
import { ChatIconButton } from '../../features/chat/components/ChatIconButton';
import { FeedbackModal } from '../../shared/components/ui/FeedbackModal';
import { useFriendRequestCount } from '../../features/friends/hooks/useFriendRequestCount';

const Navigation: React.FC = () => {
  const { user, profile, logout } = useAuth();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isViewPortfolioAsModalOpen, setIsViewPortfolioAsModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const incomingRequestCount = useFriendRequestCount();

  const isActive = (path: string) => {
    if (path === '/friends') {
      // Friends tab should be active for both /friends and /friends/:friendId
      return location.pathname === '/friends' || location.pathname.startsWith('/friends/');
    }
    if (path === '/portfolio') {
      // Portfolio tab should be active for /portfolio
      return location.pathname === '/portfolio';
    }
    return location.pathname === path;
  };

  const navItems = [
    { path: '/portfolio', label: 'Portfolio' },
    { path: '/friends', label: 'Friends' },
    // { path: '/learn', label: 'Learn' }, // TODO: Add Learn tab in future step
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  if (!user) return null;

  return (
    <nav className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <img
                className="hidden md:block h-8 w-auto"
                src="/Vester-logo-lrg-clr.png" 
                alt="Vester Large Logo"
              />
              <img
                className="block md:hidden h-8 w-auto"
                src="/Vester-logo-sm-clr.png"
                alt="Vester Small Logo"
              />
            </Link>
            <div className="flex items-center md:hidden ml-3">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                type="button"
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500"
                aria-controls="mobile-menu"
                aria-expanded={isMobileMenuOpen}
              >
                <span className="sr-only">Open main menu</span>
                <svg
                  className={`${isMobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                <svg
                  className={`${isMobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="hidden md:ml-6 md:flex md:space-x-8">
              {navItems.map(({ path, label }) => (
                <Link
                  key={path}
                  to={path}
                  className={`${
                    isActive(path)
                      ? 'border-green-500 text-gray-900'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                >
                  {label}
                  {path === '/friends' && incomingRequestCount > 0 && (
                    <span
                      className="ml-0.5 inline-block h-1.5 w-1.5 rounded-full bg-red-500 align-top -mt-3"
                      style={{ verticalAlign: 'super' }}
                      aria-label={`${incomingRequestCount} pending friend request${incomingRequestCount > 1 ? 's' : ''}`}
                    ></span>
                  )}
                </Link>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Search bar - commented out until search functionality is implemented */}
            {/* <div className="relative flex-1 max-w-xs hidden sm:block">
              <input
                type="text"
                placeholder="Search"
                className="w-full rounded-md border border-gray-300 pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg
                  className="h-4 w-4 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div> */}

            <div className="flex items-center space-x-1">
              {/* Activity bell - commented out for future use */}
              {/* <button className="relative p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-500">
                <span className="sr-only">View notifications</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <span className="absolute top-1 right-1 block h-2.5 w-2.5 transform -translate-y-1/2 translate-x-1/2 rounded-full text-red-500 ring-2 ring-white bg-red-500"></span>
              </button> */}
              <button
                onClick={() => setIsFeedbackModalOpen(true)}
                className="relative p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                title="Feedback"
              >
                <span className="sr-only">Feedback</span>
                <img
                  src="/icon-q-lrg.png"
                  alt="Feedback"
                  className="h-5 w-5"
                />
              </button>
              <ChatIconButton />
            </div>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center space-x-2"
              >
                <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                  {profile?.profile_picture_url ? (
                    <img
                      src={profile.profile_picture_url}
                      alt={`${profile.first_name || 'User'} avatar`}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <svg
                      className="h-5 w-5 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  )}
                </div>
                <svg
                  className={`h-5 w-5 text-gray-400 transition-transform ${
                    isDropdownOpen ? 'transform rotate-180' : ''
                  }`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {isDropdownOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 z-20">
                  <Link
                    to="/settings"
                    onClick={() => setIsDropdownOpen(false)}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Settings
                  </Link>
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      setIsViewPortfolioAsModalOpen(true);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    View portfolio as...
                  </button>
                  <div className="border-t border-gray-100 my-1"></div>
                  <button
                    onClick={() => {
                      logout();
                      setIsDropdownOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={`${isMobileMenuOpen ? 'block' : 'hidden'} md:hidden`} id="mobile-menu">
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          {navItems.map(({ path, label }) => (
            <Link
              key={path}
              to={path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`flex items-center px-3 py-2 rounded-md text-base font-medium ${
                isActive(path)
                  ? 'bg-green-100 text-green-700'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-800'
              }`}
            >
              {label}
              {path === '/friends' && incomingRequestCount > 0 && (
                <span
                  className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-red-500 align-top -mt-0.5"
                  style={{ verticalAlign: 'super' }}
                  aria-label={`${incomingRequestCount} pending friend request${incomingRequestCount > 1 ? 's' : ''}`}
                ></span>
              )}
            </Link>
          ))}
        </div>
      </div>

      <ViewPortfolioAsModal
        isOpen={isViewPortfolioAsModalOpen}
        onClose={() => setIsViewPortfolioAsModalOpen(false)}
      />
      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
      />
    </nav>
  );
};

export default Navigation; 